import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import UserProfile from './UserProfile';
import SubscriptionManager from './SubscriptionManager';
import APIKeyManager from './APIKeyManager';

const UserDashboard = ({ onBack }) => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const logoutGroupRef = useRef(null);

  const tabs = [
    { id: 'profile', label: 'Profile', component: UserProfile },
    { id: 'subscription', label: 'Subscription', component: SubscriptionManager },
    { id: 'api-keys', label: 'API Keys', component: APIKeyManager },
  ];

  const handleLogout = () => {
    logout();
    setShowLogoutConfirm(false);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (showLogoutConfirm) {
          setShowLogoutConfirm(false);
          return;
        }
        onBack();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onBack, showLogoutConfirm]);

  useEffect(() => {
    if (!showLogoutConfirm) return undefined;
    const handleClickOutside = (event) => {
      if (logoutGroupRef.current && !logoutGroupRef.current.contains(event.target)) {
        setShowLogoutConfirm(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLogoutConfirm]);

  const renderTab = () => {
    const tab = tabs.find(t => t.id === activeTab);
    if (tab) {
      const Component = tab.component;
      return <Component />;
    }
    return null;
  };

  return (
    <div className="user-dashboard">
      <div className="dashboard-header">
        <div className="user-info">
          <h1>Welcome, {user?.full_name || user?.email}!</h1>
          <div className="user-meta">
            <span className={`subscription-tier tier-${user?.subscription_tier}`}>
              {user?.subscription_tier} Plan
            </span>
            <span className="user-email">{user?.email}</span>
          </div>
        </div>
        <div className="dashboard-actions">
          <button 
            className="btn btn-secondary close-btn" 
            onClick={onBack}
            title="Close Account Settings"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Close
          </button>
          <div
            ref={logoutGroupRef}
            className="logout-inline-confirm"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
          >
            {!showLogoutConfirm ? (
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setShowLogoutConfirm(true)}
                title="Sign out of your account"
              >
                Sign Out
              </button>
            ) : (
              <>
                <span
                  role="alert"
                  style={{
                    fontSize: 12,
                    color: 'var(--text-muted, #6b7280)',
                    maxWidth: 220,
                    lineHeight: 1.3,
                  }}
                >
                  Sign out? You will need to sign in again to access your dashboard.
                </span>
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={handleLogout}
                  autoFocus
                  title="Confirm sign out"
                  style={{ padding: '6px 12px', fontSize: 13 }}
                >
                  Confirm sign out
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowLogoutConfirm(false)}
                  title="Cancel"
                  style={{ padding: '6px 12px', fontSize: 13 }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="dashboard-sidebar">
          <nav className="dashboard-nav">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="dashboard-main">
          {renderTab()}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
